import { useEffect, useRef } from 'react';

import { useLanguage } from '../../contexts/LanguageContext';

const SEOHead = ({
  title,
  description,
  keywords = [],
  image,
  url,
  type = 'website',
  author = 'Snappin',
  publishedTime,
  modifiedTime,
  schema
}) => {
  const { language, translations } = useLanguage();
  const previousValues = useRef({});
  
  // Valores por defecto basados en el idioma
  const defaultTitle = translations?.seo?.defaultTitle || 'Snappin - Comparte tus Memorias al Instante';
  const defaultDescription = translations?.seo?.defaultDescription || 'Snappin te permite crear eventos únicos y recopilar todas las fotos de tus invitados en un solo lugar. ¡Comparte momentos inolvidables!';
  const defaultKeywords = translations?.seo?.defaultKeywords || [
    'snappin', 'fotos', 'eventos', 'compartir', 'memorias', 'galería', 
    'wedding photos', 'event photos', 'photo sharing', 'digital memories'
  ];
  
  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords.length > 0 ? keywords : defaultKeywords;
  const finalImage = image || `${window.location.origin}/assets/brand/og-image-1200x630.png`;
  const finalUrl = url || window.location.href;

  useEffect(() => {
    // Check if values have actually changed to avoid unnecessary DOM updates
    const currentValues = {
      finalTitle,
      finalDescription,
      finalKeywords: finalKeywords.join(','),
      finalImage,
      finalUrl,
      type,
      author,
      publishedTime,
      modifiedTime,
      language,
      schema: schema ? JSON.stringify(schema) : null
    };

    // Compare with previous values
    const hasChanged = Object.keys(currentValues).some(
      key => previousValues.current[key] !== currentValues[key]
    );

    if (!hasChanged && previousValues.current.finalTitle) {
      return; // Skip update if nothing changed
    }

    // Store current values for next comparison
    previousValues.current = currentValues;

    // Batch DOM updates using requestAnimationFrame to avoid blocking navigation
    requestAnimationFrame(() => {
      try {
        // Update document title
        if (document.title !== finalTitle) {
          document.title = finalTitle;
        }
        
        // Update HTML lang attribute
        if (document.documentElement.lang !== language) {
          document.documentElement.lang = language;
        }

        // Function to update or create meta tag
        const updateMetaTag = (property, content, isProperty = false) => {
          if (!content) return;
          
          const attribute = isProperty ? 'property' : 'name';
          let meta = document.querySelector(`meta[${attribute}="${property}"]`);
          
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute(attribute, property);
            meta.setAttribute('data-seo-managed', 'true');
            document.head.appendChild(meta);
          }
          
          if (meta.getAttribute('content') !== content) {
            meta.setAttribute('content', content);
          }
        };

        // Function to update or create link tag
        const updateLinkTag = (rel, href, hreflang = null) => {
          const selector = hreflang 
            ? `link[rel="${rel}"][hreflang="${hreflang}"]` 
            : `link[rel="${rel}"]`;
          let link = document.querySelector(selector);
          
          if (!link) {
            link = document.createElement('link');
            link.rel = rel;
            link.setAttribute('data-seo-managed', 'true');
            if (hreflang) link.hreflang = hreflang;
            document.head.appendChild(link);
          }
          
          if (link.href !== href) {
            link.href = href;
          }
        };

        // Update basic meta tags
        updateMetaTag('description', finalDescription);
        updateMetaTag('keywords', finalKeywords.join(', '));
        updateMetaTag('author', author);
        updateMetaTag('robots', 'index, follow');

        // Update Open Graph meta tags
        updateMetaTag('og:type', type, true);
        updateMetaTag('og:title', finalTitle, true);
        updateMetaTag('og:description', finalDescription, true);
        updateMetaTag('og:image', finalImage, true);
        updateMetaTag('og:url', finalUrl, true);
        updateMetaTag('og:site_name', 'Snappin', true);
        updateMetaTag('og:locale', language === 'es' ? 'es_ES' : 'en_US', true);

        // Update Twitter Card meta tags
        updateMetaTag('twitter:card', 'summary_large_image');
        updateMetaTag('twitter:title', finalTitle);
        updateMetaTag('twitter:description', finalDescription);
        updateMetaTag('twitter:image', finalImage);

        // Update time-based meta tags
        if (publishedTime) {
          updateMetaTag('article:published_time', publishedTime, true);
        }
        if (modifiedTime) {
          updateMetaTag('article:modified_time', modifiedTime, true);
        }

        // Update canonical link
        updateLinkTag('canonical', finalUrl);

        // Update alternate language links
        updateLinkTag('alternate', finalUrl.replace(/\/(en|es)/, '/es'), 'es');
        updateLinkTag('alternate', finalUrl.replace(/\/(en|es)/, '/en'), 'en');
        updateLinkTag('alternate', finalUrl.replace(/\/(en|es)/, ''), 'x-default');

        // Update structured data
        if (schema) {
          let scriptTag = document.querySelector('script[type="application/ld+json"][data-seo-managed="true"]');
          if (!scriptTag) {
            scriptTag = document.createElement('script');
            scriptTag.type = 'application/ld+json';
            scriptTag.setAttribute('data-seo-managed', 'true');
            document.head.appendChild(scriptTag);
          }
          const newContent = JSON.stringify(schema);
          if (scriptTag.textContent !== newContent) {
            scriptTag.textContent = newContent;
          }
        }

      } catch (error) {
        console.warn('SEOHead: Error updating meta tags:', error);
      }
    });

  }, [finalTitle, finalDescription, finalKeywords, finalImage, finalUrl, type, author, publishedTime, modifiedTime, schema, language]);

  // This component doesn't render anything visible
  return null;
};

export default SEOHead;
